export interface SectionProgressState {
  completed: number;
  total: number;
  running: number;
  remaining: number;
  percent: number;
  visible: boolean;
}

export interface CalculateSectionProgressParams {
  settingsGroup?: any[];
  sectionStatus?: Record<string, boolean | null>;
  generatedSections?: any[];
  selectedSections?: any[];
  regeneratingSectionId?: string | null;
  isSectionWorkActive?: (section: any) => boolean;
}

/**
 * Resolves the report sections that are present in the current template/document.
 * Excludes metadata types like "base" or "project_settings", and excludes any section
 * explicitly disabled via sectionStatus[id] === false.
 */
export function getReportSections(
  settingsGroup: any[] = [],
  sectionStatus: Record<string, boolean | null> = {}
): any[] {
  if (!Array.isArray(settingsGroup) || settingsGroup.length === 0) {
    return [];
  }

  const typedSections = settingsGroup.filter((section: any) => {
    const type = String(section?.type || section?.section_type || "").toLowerCase();
    return type === "section" || type === "section_agentic";
  });

  const candidateSections =
    typedSections.length > 0
      ? typedSections
      : settingsGroup.filter((section: any) => {
          const type = String(section?.type || section?.section_type || "").toLowerCase();
          return type !== "base" && type !== "project_settings";
        });

  return candidateSections.filter((section: any) => sectionStatus[section?.id] !== false);
}

/**
 * Calculates overall section generation progress across the entire report.
 * Stays visible once a report is loaded, starting at 0 / total when none are generated,
 * and updates dynamically as sections are started, run, and complete.
 */
export function calculateSectionProgress({
  settingsGroup = [],
  sectionStatus = {},
  generatedSections = [],
  selectedSections = [],
  regeneratingSectionId = null,
  isSectionWorkActive,
}: CalculateSectionProgressParams): SectionProgressState {
  const reportSections = getReportSections(settingsGroup, sectionStatus);
  const total = reportSections.length;

  if (total === 0) {
    return {
      completed: 0,
      total: 0,
      running: 0,
      remaining: 0,
      percent: 0,
      visible: false,
    };
  }

  const selectedSectionIds = new Set(
    (selectedSections || []).map((section: any) => section?.id).filter(Boolean)
  );

  const checkWorkActive =
    typeof isSectionWorkActive === "function"
      ? isSectionWorkActive
      : (section: any) =>
          Boolean(
            section?.chatAsyncInProgress ||
              section?.chatAsyncRegenerationInProgress ||
              section?.agenticInProgress ||
              section?.agenticRegenerationInProgress
          );

  const completed = reportSections.filter((reportSection: any) => {
    const id = reportSection?.id;
    const generatedSection = (generatedSections || []).find((item: any) => item?.id === id);
    return (
      Boolean(generatedSection) &&
      !checkWorkActive(generatedSection) &&
      !selectedSectionIds.has(id) &&
      id !== regeneratingSectionId &&
      Boolean(generatedSection.content || generatedSection.chat_history_id)
    );
  }).length;

  const running = reportSections.filter((reportSection: any) => {
    const id = reportSection?.id;
    const generatedSection = (generatedSections || []).find((item: any) => item?.id === id);
    return (
      (Boolean(generatedSection) && checkWorkActive(generatedSection)) ||
      selectedSectionIds.has(id) ||
      id === regeneratingSectionId
    );
  }).length;

  const remaining = Math.max(0, total - completed);
  const percent = Math.round((completed / total) * 100);

  return {
    completed,
    total,
    running,
    remaining,
    percent,
    visible: true,
  };
}
